'use client';

import { DotLottie, DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useBunja } from 'bunja/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';

import { contentfulImageUrl } from '../assets';
import { zIndex } from './constants';
import { rollAndMoveBunja } from './state';

interface RewardBoxProps {
  prizeId: string;
  isGoal: boolean;
  onClose: () => void;
  skin?: string | null;
}

const spinDurationMs = 2000;
const decelDurationMs = 1000;
const revealPlayDurationMs = 2000;

export const RewardBox = ({ prizeId, isGoal, onClose, skin }: RewardBoxProps) => {
  const { rollAndDiceSectionLayout } = useBunja(rollAndMoveBunja);
  const [phase, setPhase] = useState<'spinning' | 'decelerating' | 'revealed'>('spinning');
  const [isLottieLoaded, setIsLottieLoaded] = useState(false);
  const lottieRef = useRef<DotLottie | null>(null);

  const prizes = isGoal
    ? rollAndDiceSectionLayout?.goalPrizesCollection?.items
    : rollAndDiceSectionLayout?.prizesCollection?.items;
  const prize = prizes?.find((p) => p?.id === prizeId);
  const slotPrizes = useMemo(() => prizes?.filter((p) => p?.image?.url) ?? [], [prizes]);

  const targetIndex = useMemo(() => {
    const idx = slotPrizes.findIndex((p) => p?.id === prizeId);
    return idx >= 0 ? idx : 0;
  }, [slotPrizes, prizeId]);

  const isLottie = skin?.endsWith('.lottie');

  const isReady = !isLottie || isLottieLoaded;

  const handleSlotComplete = useCallback(() => {
    setPhase('revealed');
    if (lottieRef.current) {
      lottieRef.current.play();
    }
    setTimeout(() => {
      onClose();
    }, revealPlayDurationMs);
  }, [onClose]);

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setPhase('decelerating');
    }, spinDurationMs);

    const timer2 = setTimeout(() => {
      handleSlotComplete();
    }, spinDurationMs + decelDurationMs);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [handleSlotComplete]);

  return (
    <div style={{ visibility: isReady ? 'visible' : 'hidden' }}>
      <div
        className={css({
          position: 'absolute',
          inset: 0,
          bg: 'rgba(0, 0, 0, 0.6)',
          zIndex: zIndex.dimmed,
        })}
      />

      <div
        className={center({
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: zIndex.rewardBox,
          w: 275,
          h: 275,
        })}
      >
        <div
          className={css({
            w: '100%',
            h: '100%',
            position: 'relative',
          })}
        >
          {skin && (
            <div
              className={css({
                position: 'absolute',
                inset: 0,
                zIndex: zIndex.rewardBoxSkin,
                pointerEvents: 'none',
              })}
            >
              {isLottie ? (
                <DotLottieReact
                  src={skin}
                  loop={false}
                  autoplay={false}
                  dotLottieRefCallback={(dotLottie) => {
                    lottieRef.current = dotLottie;
                    dotLottie?.addEventListener('load', () => {
                      setIsLottieLoaded(true);
                    });
                  }}
                  className={css({ w: '100%', h: '100%' })}
                />
              ) : (
                <img
                  src={contentfulImageUrl(skin)}
                  alt='reward box'
                  className={css({ w: '100%', h: '100%', objectFit: 'contain' })}
                />
              )}
            </div>
          )}

          <p
            className={css({
              position: 'absolute',
              left: '50%',
              top: 52.5,
              transform: 'translateX(-50%)',
              w: 141,
              textStyle: 'Text/M/Strong',
              color: 'inverse.white',
              textAlign: 'center',
              zIndex: zIndex.rewardBoxText,
              whiteSpace: 'pre-wrap',
            })}
          >
            {phase === 'revealed' ? (
              <FormattedMessage id='Campaign.r260204.You_won_prize' values={{ prize: prize?.name ?? '' }} />
            ) : (
              <FormattedMessage id='Campaign.r260204.Good_luck_incoming' />
            )}
          </p>

          <div
            className={css({
              position: 'absolute',
              left: '50%',
              top: 120.5,
              transform: 'translateX(-50%)',
              w: 100,
              h: 100,
              overflow: 'hidden',
              borderRadius: 8,
              zIndex: zIndex.rewardBoxSlot,
            })}
          >
            <SlotMachine prizes={slotPrizes} targetIndex={targetIndex} phase={phase} />
          </div>
        </div>
      </div>
    </div>
  );
};

interface SlotMachineProps {
  prizes: Array<{ id?: string | null; image?: { url?: string | null } | null; name?: string | null } | null>;
  targetIndex: number;
  phase: 'spinning' | 'decelerating' | 'revealed';
}

const SlotMachine = ({ prizes, targetIndex, phase }: SlotMachineProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);
  const startOffsetRef = useRef(0);
  const [itemSize, setItemSize] = useState(0);

  const totalHeight = prizes.length * itemSize;

  const targetOffset = targetIndex * itemSize;

  useEffect(() => {
    const updateSize = () => {
      if (wrapperRef.current) {
        const size = wrapperRef.current.offsetWidth;
        setItemSize(size);
      }
    };

    requestAnimationFrame(updateSize);

    const resizeObserver = new ResizeObserver(updateSize);
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || prizes.length === 0 || itemSize === 0) return;

    let animationId: number;
    const spinSpeed = itemSize * 0.12;

    const animate = (timestamp: number) => {
      if (phase === 'spinning') {
        offsetRef.current = (offsetRef.current + spinSpeed) % totalHeight;
        container.style.transform = `translateY(-${offsetRef.current}px)`;
        animationId = requestAnimationFrame(animate);
      } else if (phase === 'decelerating') {
        if (startTimeRef.current === null) {
          startTimeRef.current = timestamp;
          startOffsetRef.current = offsetRef.current;
        }

        const elapsed = timestamp - startTimeRef.current;
        const progress = Math.min(elapsed / decelDurationMs, 1);

        const eased = 1 - Math.pow(1 - progress, 3);

        const extraDistance = totalHeight * 2;
        const finalTarget = targetOffset + extraDistance;
        const totalDistance = finalTarget - startOffsetRef.current;

        offsetRef.current = startOffsetRef.current + totalDistance * eased;
        container.style.transform = `translateY(-${offsetRef.current}px)`;

        if (progress < 1) {
          animationId = requestAnimationFrame(animate);
        }
      } else {
        container.style.transform = `translateY(-${targetOffset + totalHeight * 2}px)`;
      }
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [phase, prizes.length, totalHeight, targetOffset, itemSize]);

  useEffect(() => {
    if (phase === 'spinning') {
      startTimeRef.current = null;
      startOffsetRef.current = 0;
    }
  }, [phase]);

  if (prizes.length === 0) return null;

  const displayPrizes = [...prizes, ...prizes, ...prizes, ...prizes, ...prizes];

  return (
    <div ref={wrapperRef} className={css({ w: '100%', h: '100%', position: 'relative', overflow: 'hidden' })}>
      {itemSize > 0 && (
        <div ref={containerRef} className={css({ display: 'flex', flexDirection: 'column', alignItems: 'center' })}>
          {displayPrizes.map((p, idx) =>
            p?.image?.url ? (
              <img
                key={idx}
                src={contentfulImageUrl(p.image.url)}
                alt={p.name ?? 'prize'}
                style={{ width: itemSize, height: itemSize }}
                className={css({
                  objectFit: 'contain',
                  flexShrink: 0,
                })}
              />
            ) : null,
          )}
        </div>
      )}
    </div>
  );
};
